/**
 * Cross-window sync + saved popout layouts.
 *
 * `BroadcastChannel` is same-origin and works across tabs/windows — perfect
 * for keeping the main dashboard in step with popped-out receive screens
 * without any server round-trip.
 */

const CHANNEL_NAME = "jc-sync";
const LAYOUT_KEY = "jc_popout_layout_v1";

let channel = null;
const getChannel = () => {
  if (typeof window === "undefined") return null;
  if (channel) return channel;
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    channel = null;
  }
  return channel;
};

export const broadcast = (type, payload = {}) => {
  const c = getChannel();
  if (!c) return;
  try {
    c.postMessage({ type, payload, at: Date.now() });
  } catch {
    /* channel closed */
  }
};

/**
 * subscribe(handler): handler receives {type, payload, at}.
 * Returns an unsubscribe function.
 */
export const subscribe = (handler) => {
  const c = getChannel();
  if (!c) return () => {};
  const cb = (evt) => {
    try {
      handler(evt.data);
    } catch (e) {
      /* ignore */
    }
  };
  c.addEventListener("message", cb);
  return () => c.removeEventListener("message", cb);
};

// ---------- Popout layout persistence ----------

const readLayout = () => {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLayout = (entries) => {
  try {
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(entries.slice(0, 6)));
  } catch {
    /* quota exceeded */
  }
};

export const getSavedLayout = () => readLayout();
export const clearSavedLayout = () => {
  try { localStorage.removeItem(LAYOUT_KEY); } catch { /* noop */ }
};

/**
 * Track this window as a popout: called from the popped-out window itself.
 * Persists { path, x, y, w, h } into localStorage on unload / blur.
 */
export const trackPopoutWindow = (path) => {
  if (typeof window === "undefined") return () => {};
  const save = () => {
    try {
      const entries = readLayout().filter((e) => e.path !== path);
      entries.unshift({
        path,
        x: window.screenX,
        y: window.screenY,
        w: window.outerWidth,
        h: window.outerHeight,
        at: Date.now(),
      });
      writeLayout(entries);
    } catch { /* noop */ }
  };
  save();
  const onBeforeUnload = () => save();
  const onBlur = () => save();
  window.addEventListener("beforeunload", onBeforeUnload);
  window.addEventListener("blur", onBlur);
  // Also poll every 3s so we survive OS-level force-close
  const poll = setInterval(save, 3000);
  return () => {
    window.removeEventListener("beforeunload", onBeforeUnload);
    window.removeEventListener("blur", onBlur);
    clearInterval(poll);
  };
};

/**
 * Remember a popout when the user first opens one (called from PageHeader).
 */
export const rememberPopout = ({ path, x, y, w, h }) => {
  const entries = readLayout().filter((e) => e.path !== path);
  entries.unshift({ path, x, y, w, h, at: Date.now() });
  writeLayout(entries);
};

/**
 * Reopen every saved popout window. Must be invoked from a user gesture
 * (button click) so browsers honour the `window.open` call.
 * Returns the number of windows we attempted to open.
 */
export const restoreSavedLayout = () => {
  const entries = readLayout();
  let opened = 0;
  for (const e of entries) {
    try {
      const url = new URL(e.path, window.location.origin);
      url.searchParams.set("pop", "1");
      const name = `jc-${e.path.replace(/\//g, "-")}`;
      const features = `popup=yes,noopener=no,width=${Math.max(400, e.w || 1200)},height=${Math.max(400, e.h || 800)},left=${e.x || 40},top=${e.y || 40}`;
      const w = window.open(url.toString(), name, features);
      if (w) {
        opened++;
        // Nudge position after open — some browsers ignore left/top on first call
        try { w.moveTo(e.x, e.y); w.resizeTo(e.w, e.h); } catch { /* noop */ }
      }
    } catch { /* skip malformed */ }
  }
  return opened;
};
